import { useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { useArchivePlan, useCompletePlan, useDeletePlan, usePlan, usePlans, useRenamePlan, type PlanDetail, type PlanRow, type Progress } from "../../api/routing";
import { StatusChip, type Tone } from "../../components/ui";
import "../../styles/routing.css";
import PlanDay from "./PlanDay";
import RoutingTabs from "./RoutingTabs";

export const STATE_LABEL: Record<string, string> = { proposal: "Proposal", accepted: "Accepted", completed: "Completed", archived: "Archived" };
export const STATE_TONE: Record<string, Tone> = { proposal: "draft", accepted: "ok", completed: "info", archived: "quiet" };

/** Saved plans: the list on the left, newest first, archived ones behind a
 * switch, each with how far its stops have got; the chosen plan's days and
 * stops on the right, with what became of each and what to do next. */
export default function PlansPage() {
  const { id } = useParams();
  const selected = id === undefined ? null : Number(id);
  const [params, setParams] = useSearchParams();
  const archived = params.get("archived") === "on";
  const list = usePlans(archived);
  const rows = list.data?.rows ?? [];
  const suffix = archived ? "?archived=on" : "";
  return (
    <div className="page-bleed routing-page">
      <RoutingTabs />
      <div className="split wide-rail">
        <aside className="split-rail plans-rail" aria-label="Route plans">
          <div className="plans-rail-head">
            <h2>Route plans</h2>
            <span className="count-badge">{rows.length}</span>
            <label className="check">
              <input type="checkbox" checked={archived} onChange={(e) => setParams(e.target.checked ? { archived: "on" } : {})} /> Archived
            </label>
          </div>
          {list.isPending ? <p className="empty-state">Loading plans…</p> : null}
          {list.isError ? <p className="notice bad">The plans could not be loaded: {list.error.message}</p> : null}
          {list.data && rows.length === 0 ? (
            <p className="empty-state">{archived ? "Nothing archived." : "No plans saved yet. Plan the routes on the Planner and save them."}</p>
          ) : null}
          {rows.map((row) => (
            <PlanListRow key={row.id} row={row} current={row.id === selected} to={`/routing/plans/${row.id}${suffix}`} />
          ))}
        </aside>
        <section className="split-main" aria-label="Plan">
          {selected === null ? (
            <p className="empty-state">{rows.length > 0 ? "Choose a plan on the left." : "A saved plan opens here."}</p>
          ) : (
            <PlanWorkspace id={selected} />
          )}
        </section>
      </div>
    </div>
  );
}

function PlanListRow({ row, current, to }: { row: PlanRow; current: boolean; to: string }) {
  const p = row.progress;
  return (
    <Link to={to} className="plan-row" aria-current={current ? "page" : undefined}>
      <span className="plan-row-top">
        <span className="plan-row-name">{row.name}</span>
        <StatusChip tone={STATE_TONE[row.state]}>{STATE_LABEL[row.state] ?? row.state}</StatusChip>
      </span>
      <ProgressMeter progress={p} />
      <span className="plan-row-meta">
        {row.contract} · {p.days} day{p.days === 1 ? "" : "s"} · {p.stops} stops · {p.done} done · {p.not_done} not done · {formatDate(row.created_at)}
      </span>
    </Link>
  );
}

/** Done in green, not done in amber, what is still pending left empty. */
export function ProgressMeter({ progress }: { progress: Progress }) {
  const share = (n: number) => (progress.stops === 0 ? "0%" : `${(n / progress.stops) * 100}%`);
  return (
    <span className="plan-meter" role="img" aria-label={`${progress.done} done, ${progress.not_done} not done, ${progress.pending} pending`}>
      <span className="done" style={{ width: share(progress.done) } as CSSProperties} />
      <span className="not-done" style={{ width: share(progress.not_done) } as CSSProperties} />
    </span>
  );
}

function PlanWorkspace({ id }: { id: number }) {
  const plan = usePlan(id);
  if (plan.isPending) {
    return <p className="app-loading">Loading plan…</p>;
  }
  if (plan.isError) {
    return <p className="notice bad">The plan could not be loaded: {plan.error.message}</p>;
  }
  return <PlanDetailView detail={plan.data} />;
}

/** The plan's name, edited where it is shown. Saved with Save name or Enter;
 * Cancel or Escape leaves it as it was. A blank name cannot be saved. */
function RenamePlan({ id, name, onDone }: { id: number; name: string; onDone: () => void }) {
  const rename = useRenamePlan(id);
  const [value, setValue] = useState(name);
  const cleaned = value.trim();
  return (
    <form
      className="plan-rename"
      onSubmit={(e) => {
        e.preventDefault();
        if (cleaned === name) {
          onDone();
        } else if (cleaned !== "") {
          rename.mutate(cleaned, { onSuccess: onDone });
        }
      }}
    >
      <input
        aria-label="Plan name"
        value={value}
        maxLength={120}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onDone();
          }
        }}
      />
      <button type="submit" className="btn btn-primary" disabled={cleaned === "" || rename.isPending}>
        {rename.isPending ? "Saving…" : "Save name"}
      </button>
      <button type="button" className="btn" disabled={rename.isPending} onClick={onDone}>
        Cancel
      </button>
      {rename.isError ? <p className="notice bad">{rename.error.message}</p> : null}
    </form>
  );
}

/** Deleting is asked twice, and says what goes with the plan: its recorded
 * visits leave the visits report. What it filed stays on the sites. */
function DeletePlan({ detail, onKeep }: { detail: PlanDetail; onKeep: () => void }) {
  const remove = useDeletePlan(detail.plan.id);
  const navigate = useNavigate();
  const recorded = detail.progress.done + detail.progress.not_done;
  return (
    <div className="notice warn plan-delete" role="group" aria-label={`Delete ${detail.plan.name}?`}>
      <p>
        <strong>Delete {detail.plan.name} for good?</strong> Its {detail.progress.days} day{detail.progress.days === 1 ? "" : "s"} and{" "}
        {detail.progress.stops} stop{detail.progress.stops === 1 ? "" : "s"} go, and this cannot be undone.
        {recorded > 0 ? ` ${recorded} recorded visit${recorded === 1 ? " leaves" : "s leave"} the visits report.` : ""} Job cards and inspections stay on
        their sites.
      </p>
      <div className="move-buttons">
        <button
          type="button"
          className="btn btn-danger"
          disabled={remove.isPending}
          onClick={() => remove.mutate(undefined, { onSuccess: () => navigate("/routing/plans?archived=on", { replace: true }) })}
        >
          {remove.isPending ? "Deleting…" : "Delete for good"}
        </button>
        <button type="button" className="btn" disabled={remove.isPending} onClick={onKeep}>
          Keep it
        </button>
      </div>
      {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
    </div>
  );
}

/** One plan: its state and progress, the day sheets, completing or archiving
 * it, and a card per day. An archived plan is read-only until restored, and
 * can be deleted. */
export function PlanDetailView({ detail }: { detail: PlanDetail }) {
  const plan = detail.plan;
  const archive = useArchivePlan(plan.id);
  const complete = useCompletePlan(plan.id);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const editable = plan.editable;
  const p = detail.progress;
  return (
    <section className="plan-detail" aria-label={`Plan ${plan.name}`}>
      <header className="plan-head">
        {renaming ? (
          <RenamePlan id={plan.id} name={plan.name} onDone={() => setRenaming(false)} />
        ) : (
          <>
            <h1>{plan.name}</h1>
            {editable ? (
              <button type="button" className="row-button" onClick={() => setRenaming(true)}>
                Rename
              </button>
            ) : null}
          </>
        )}
        <StatusChip tone={STATE_TONE[plan.state]}>{STATE_LABEL[plan.state] ?? plan.state}</StatusChip>
        <span className="plan-head-meta">
          {plan.contract} · {p.days_complete}/{p.days} days complete · {p.done} done · {p.not_done} not done · {p.pending} pending
        </span>
        <div className="plan-head-actions">
          <Link className="btn" to={`/routing/${plan.id}/daysheets`}>
            Day sheets
          </Link>
          {editable ? (
            <>
              <button type="button" className="btn btn-secondary" disabled={complete.isPending} onClick={() => complete.mutate(plan.state === "completed")}>
                {plan.state === "completed" ? "Reopen" : "Mark complete"}
              </button>
              <button type="button" className="btn" disabled={archive.isPending} onClick={() => archive.mutate(false)}>
                Archive
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" disabled={archive.isPending} onClick={() => archive.mutate(true)}>
                Restore
              </button>
              <button type="button" className="btn btn-danger" disabled={deleting} onClick={() => setDeleting(true)}>
                Delete
              </button>
            </>
          )}
        </div>
      </header>
      {deleting ? <DeletePlan detail={detail} onKeep={() => setDeleting(false)} /> : null}
      {archive.isError ? <p className="notice bad">{archive.error.message}</p> : null}
      {complete.isError ? <p className="notice bad">{complete.error.message}</p> : null}
      {!editable ? <p className="notice">Archived: off the schedule and read-only. Restore it to act on it again.</p> : null}
      {detail.days.map((day) => (
        <PlanDay key={day.day_index} detail={detail} dayIndex={day.day_index} />
      ))}
    </section>
  );
}
